import * as React from 'react';
import { useEffectReducer } from 'use-effect-reducer';
import produce from 'immer';
import { evaluateRules } from './rulesParser';
import { count } from '../../../lab/utils';

function isPeopleInfo(value) {
  return Array.isArray(value.count);
}

export function useDefaultPeopleSelection(options, defaultSelection) {
  return React.useMemo(() => {
    if (isPeopleInfo(defaultSelection)) {
      return defaultSelection;
    }
    return {
      count: options.map((v) => defaultSelection[v.name] ?? 0)
    };
  }, [defaultSelection, options]);
}

export function usePassengers(dispatchProps, passengers = { options: [], rules: [] }) {
  const maxCount = passengers.options.map((v) => v.maxCount);
  const minCount = passengers.options.map((v) => v.minCount ?? 0);
  const initialState = useDefaultPeopleSelection(passengers.options, {
    count: [1, 0, 0, 0, 0, 0, 0]
  }); //passengers.defaultSelection

  const [passengersValue, setPassengersValue] = React.useState(initialState);

  const evaluator = evaluateRules(passengers.rules, passengers);

  const onChange = (value) => {
    setPassengersValue(value);
    dispatchProps({
      type: 'PASSENGERS_CHANGE',
      payload: value.count
    });
  };

  const [state, dispatch] = useEffectReducer((prevState, event, exec) => {
    const newState = produce(prevState, (draft) => {
      const { ruleActions } = evaluator(prevState, event.type, event.index);

      // eslint-disable-next-line default-case
      switch (event.type) {
        case 'ADD': {
          const index = event.index;
          if (isEventValid(event, prevState)) {
            draft.count[index] += 1;
          }
          break;
        }
        case 'REMOVE': {
          const index = event.index;
          if (isEventValid(event, prevState)) {
            draft.count[index] -= 1;
            ruleActions(draft);
          }
          break;
        }
        case 'VALUE_SET': {
          setPassengersValue(event.value);
          return event.value;
        }
      }
    });
    // exec(() => onChange?.(newState));
    return newState;
  }, initialState);

  function isEventValid(event, currentState) {
    const totalCount = count(currentState.count);

    const canAddChild = totalCount < passengers.totalPassengers;

    const { valid } = evaluator(currentState, event.type, event.index);
    switch (event.type) {
      case 'ADD': {
        const index = event.index;
        return (
          valid &&
          canAddChild &&
          (maxCount[index] === undefined || currentState.count[index] < maxCount[index])
        );
      }
      case 'REMOVE': {
        const index = event.index;
        let min = currentState.count[2] + currentState.count[3];
        if (index === 0) {
          if (min > 0 && currentState.count[index] > 0) {
            return currentState.count[index] + currentState.count[4] > min;
          } else if (currentState.count[4] > 0) {
            return currentState.count[index] > 0;
          }
        }
        if (index === 4) {
          if (min > 0 && currentState.count[index] > 0) {
            return currentState.count[index] + currentState.count[0] > min;
          } else if (currentState.count[0] > 0) {
            return currentState.count[index] > 0;
          } else {
            return currentState.count[index] > 1;
          }
        }

        return currentState.count[index] > minCount[index];
      }
      default: {
        return true;
      }
    }
  }
  return {
    value: state,
    valueCount: passengersValue,
    dispatch,
    options: passengers.options,
    count: count(passengersValue.count),
    isEventValid,
    onChange
  };
}
